import type { LocalizedText } from "@/math";

export interface NpcDef {
  id: string;
  name: LocalizedText;
  texture: string;
  /** Portrait URL (38×38) for dialog boxes. */
  faceset: string;
  lines: LocalizedText[];
}

const npc = (id: string, pl: string, en: string, sheet: string, lines: Array<[string, string]>): NpcDef => ({
  id,
  name: { pl, en },
  texture: `char-${sheet.toLowerCase()}`,
  faceset: `assets/ninja/facesets/${sheet}.png`,
  lines: lines.map(([p, e]) => ({ pl: p, en: e })),
});

export const NPCS: Record<string, NpcDef> = {
  // ---- Countinghollow ----
  abacia: npc("abacia", "Starsza Abacja", "Elder Abacia", "OldWoman", [
    [
      "Witaj, {name}! Wielka Księga została rozdarta i liczby przestały się zgadzać.",
      "Welcome, {name}! The Great Ledger has been torn and the numbers no longer add up.",
    ],
    [
      "Na łące pojawiły się Błędy — potwory z pomyłek. Zrani je tylko poprawna odpowiedź.",
      "Errors have appeared in the meadow — monsters made of mistakes. Only a correct answer can hurt them.",
    ],
    [
      "Weź Różdżkę Odpowiedzi. Podejdź do potwora, odpowiadaj mądrze i szybko — a jeśli się pomylisz, patrz, jak to policzyć.",
      "Take the Wand of Answers. Walk up to a monster, answer wisely and quickly — and if you slip, look at how it should be solved.",
    ],
    [
      "Uważaj na Rycerza z Puszki przy stawie. To sardynka. W zbroi. Nie pytaj.",
      "Beware the Tin Knight by the pond. It is a sardine. In armour. Do not ask.",
    ],
  ]),
  quill: npc("quill", "Mag Kwil", "Wizard Quill", "SorcererOrange", [
    [
      "Witaj, {name}! Jestem Kwil, mag nauki. Zanim ruszysz na potwory, możesz poćwiczyć u mnie — bez pośpiechu i bez guzów.",
      "Greetings, {name}! I am Quill, wizard of learning. Before you face the monsters you can practise with me — no rush, no bruises.",
    ],
    [
      "Wybierz temat, a ja będę zadawać pytania i pokazywać, jak je rozwiązać.",
      "Pick a topic and I will ask questions and show you how to solve them.",
    ],
  ]),
  milo: npc("milo", "Kupiec Milo", "Merchant Milo", "Villager2", [
    [
      "Witaj w moim sklepie! Mikstury, zwoje z podpowiedziami… wszystko, czego potrzebuje młody bohater.",
      "Welcome to my shop! Potions, hint scrolls… everything a young hero needs.",
    ],
    [
      "Psst — w ogródku za żywopłotem ktoś zostawił skrzynię.",
      "Psst — someone left a chest in the garden behind the hedge.",
    ],
  ]),
  antek: npc("antek", "Antek", "Antek", "Villager", [
    [
      "Cześć, {name}! Też chcę zostać bohaterem, ale mama każe mi najpierw odrobić lekcje.",
      "Hi {name}! I want to be a hero too, but mum says homework first.",
    ],
    [
      "W lesie na wschodzie mieszka gajowy Borys. Podobno widział sowy, które zadają pytania!",
      "Forester Borys lives in the forest to the east. They say he has seen owls that ask questions!",
    ],
  ]),

  // ---- Whispering Forest ----
  borys: npc("borys", "Gajowy Borys", "Forester Borys", "Hunter", [
    [
      "Szepczący Las nie jest już bezpieczny. Sowy Sprawdziany, Węże Ułamki… a w głębi mieszka Niedźwiedź Bez Reszty.",
      "The Whispering Forest is no longer safe. Quiz Owls, Fraction Snakes… and deep inside lives the Remainder Bear.",
    ],
    [
      "Ścieżka na południe prowadzi na wybrzeże. Trzymaj się jej, w gęstwinie łatwo zabłądzić.",
      "The path south leads to the coast. Stay on it, the thicket is easy to get lost in.",
    ],
  ]),

  // ---- Ruins of Old Reckoning & Bead Mine ----
  tomik: npc("tomik", "Skryba Tomik", "Scribe Tomik", "Monk", [
    [
      "To ruiny Starej Rachuby. Kiedyś liczono tu wszystko — dziś liczą tylko szkielety.",
      "These are the ruins of Old Reckoning. Everything used to be counted here — today only the skeletons count.",
    ],
    [
      "Zbieram kartki z Wielkiej Księgi. Prawdziwy rachmistrz nie myli się osiem razy z rzędu — pamiętaj o tym w walce.",
      "I collect pages of the Great Ledger. A true reckoner does not slip eight times in a row — remember that in a fight.",
    ],
  ]),
  kazik: npc("kazik", "Górnik Kazik", "Miner Kazik", "OldMan2", [
    [
      "Kopalnia Koralików jest zamknięta, odkąd krety przegryzły liny. A w środku został mój kilof!",
      "The Bead Mine has been closed since the moles chewed through the ropes. And my pickaxe is still inside!",
    ],
    [
      "Uważaj na nietoperze — wszystko, co mają, jest na minusie.",
      "Mind the bats — everything they own is in the minus.",
    ],
  ]),

  // ---- Cannery Coast ----
  marta: npc("marta", "Rybaczka Marta", "Fisher Marta", "Woman", [
    [
      "Witaj na Wybrzeżu Konserw! Odkąd sardynki założyły zbroje, ryby nie chcą wchodzić do sieci.",
      "Welcome to the Cannery Coast! Since the sardines put on armour, the fish refuse to swim into the nets.",
    ],
    [
      "Na wschodnim krańcu plaży obozuje ich Sierżant. Nie chodź tam bez mikstur.",
      "Their Sergeant camps at the east end of the beach. Do not go there without potions.",
    ],
  ]),
  mila: npc("mila", "Kapitan Mila", "Captain Mila", "Knight", [
    [
      "Pilnuję drogi na wybrzeże. Blaszany Legion maszeruje po plaży w tę i z powrotem, jakby liczył kroki.",
      "I guard the road to the coast. The Tin Legion marches up and down the beach as if counting its steps.",
    ],
    [
      "Kto pokona trzech Rycerzy z Puszki, ten może nazywać się rycerzem.",
      "Whoever defeats three Tin Knights may call themselves a knight.",
    ],
  ]),
};

/** Sprite sheet files (under assets/ninja/characters) keyed by texture name. */
export const NPC_SHEETS: Record<string, string> = {
  "char-oldwoman": "OldWoman.png",
  "char-sorcererorange": "SorcererOrange.png",
  "char-villager2": "Villager2.png",
  "char-villager": "Villager.png",
  "char-hunter": "Hunter.png",
  "char-monk": "Monk.png",
  "char-oldman2": "OldMan2.png",
  "char-woman": "Woman.png",
  "char-knight": "Knight.png",
};
